import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormArray, FormControl, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { UserConfigService } from '@core/services';
import { NotificationService } from '@core/services';
import { AmplitudeService } from '@core/services';
import { MainWrapperComponent } from '@core/component';

@Component({
  selector: 'app-options',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, MainWrapperComponent],
  templateUrl: './options.component.html',
  styleUrls: ['./options.component.scss']
})
export class OptionsComponent implements OnInit {
  optionsForm!: FormGroup;
  isLoading = false;
  isSaving = false;
  userDefaultDomain: string | null = null;
  newBcDomain: string = '';
  newIgnoredDomain: string = '';
  bcDomainEnabledState: Map<number, boolean> = new Map();

  // List of free email domains that should not have the toggle option
  private readonly FREE_EMAIL_DOMAINS = [
    'gmail.com', 'yahoo.com', 'outlook.com', 'hotmail.com', 
    'icloud.com', 'aol.com', 'protonmail.com', 'mail.com',
    'zoho.com', 'yandex.com', 'gmx.com', 'live.com'
  ];

  constructor(
    private fb: FormBuilder,
    private userConfigService: UserConfigService,
    private notificationService: NotificationService,
    private amplitudeService: AmplitudeService
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.loadConfig();
  }

  private initForm(): void {
    // Get user's default domain first
    this.userDefaultDomain = this.userConfigService.getUserDefaultDomain();
    
    this.optionsForm = this.fb.group({
      ignoredDomains: this.fb.array([]),
      bcIgnoreDefaultDomain: [{ value: this.isFreeEmailDomain, disabled: this.isFreeEmailDomain }],
      bcDomains: this.fb.array([])
    });
  }

  get ignoredDomainsArray(): FormArray {
    return this.optionsForm.get('ignoredDomains') as FormArray;
  }

  get bcDomainsArray(): FormArray {
    return this.optionsForm.get('bcDomains') as FormArray;
  }

  get bcIgnoreDefaultDomain(): FormControl {
    return this.optionsForm.get('bcIgnoreDefaultDomain') as FormControl;
  }

  get isFreeEmailDomain(): boolean {
    if (!this.userDefaultDomain) return false;
    return this.FREE_EMAIL_DOMAINS.includes(this.userDefaultDomain.toLowerCase());
  }

  private async loadConfig(): Promise<void> {
    this.isLoading = true;
    try {
      // Load ignored domains
      const ignoredDomains = await firstValueFrom(this.userConfigService.loadIgnoredDomains());
      this._setDomainControls(this.ignoredDomainsArray, ignoredDomains);

      // Load battle card domains
      const bcConfig = await this.userConfigService.loadBattleCardDomains();
      this.bcIgnoreDefaultDomain.setValue(bcConfig.ignoreDefault);
      
      // Only load domains if they exist
      if (bcConfig.domains.length > 0) {
        this._setDomainControls(this.bcDomainsArray, bcConfig.domains);
      }

      this.optionsForm.markAsPristine();
    } catch (error) {
      console.error('Error loading config:', error);
      this.notificationService.notify('Failed to load settings');
    } finally {
      this.isLoading = false;
    }
  }

  private _setDomainControls(formArray: FormArray, domains: string[]): void {
    formArray.clear();
    this.bcDomainEnabledState.clear();
    domains.forEach((domain, index) => {
      // Determine if this is for ignored domains or bc domains
      const isIgnoredDomains = formArray === this.ignoredDomainsArray;
      const control = isIgnoredDomains 
        ? this.createStrictDomainControl(domain)
        : this.createUrlControl(domain);
      formArray.push(control);
      if (!isIgnoredDomains) {
        this.bcDomainEnabledState.set(index, true);
      }
    });
  }

  private createStrictDomainControl(value: string = ''): FormControl {
    return this.fb.control(value, [
      Validators.pattern(/^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/)
    ]);
  }

  private createUrlControl(value: string = ''): FormControl {
    // Relaxed validation - accepts domains or full URLs
    return this.fb.control(value);
  }

  toggleDefaultDomain(): void {
    // Invert the logic: checked = include (false), unchecked = ignore (true)
    const currentValue = this.bcIgnoreDefaultDomain.value;
    this.bcIgnoreDefaultDomain.setValue(!currentValue);
    this.optionsForm.markAsDirty();
  }

  addNewBcDomain(): void {
    const url = this.newBcDomain.trim();
    if (!url) return;

    // Check for duplicates
    const existingUrls = this.bcDomainsArray.controls.map(c => c.value.toLowerCase());
    if (existingUrls.includes(url.toLowerCase())) {
      this.notificationService.notify('URL already exists');
      return;
    }

    // No strict validation - accept any non-empty string
    this.bcDomainsArray.push(this.createUrlControl(url));
    const newIndex = this.bcDomainsArray.length - 1;
    this.bcDomainEnabledState.set(newIndex, true);
    this.newBcDomain = '';
    this.optionsForm.markAsDirty();
    this.optionsForm.markAsTouched();
  }

  addNewIgnoredDomain(): void {
    const domain = this.newIgnoredDomain.trim();
    if (!domain) return;

    // Check for duplicates
    const existingDomains = this.ignoredDomainsArray.controls.map(c => c.value.toLowerCase());
    if (existingDomains.includes(domain.toLowerCase())) {
      this.notificationService.notify('Domain already exists');
      return;
    }

    // Strict domain validation
    const domainPattern = /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    if (!domainPattern.test(domain)) {
      this.notificationService.notify('Invalid domain format. Use format: company.com');
      return;
    }

    this.ignoredDomainsArray.push(this.createStrictDomainControl(domain));
    this.newIgnoredDomain = '';
    this.optionsForm.markAsDirty();
    this.optionsForm.markAsTouched();
  }

  removeIgnoredDomainField(index: number): void {
    this.ignoredDomainsArray.removeAt(index);
    this.optionsForm.markAsDirty();
    this.optionsForm.markAsTouched();
  }

  toggleBcDomain(index: number): void {
    const currentState = this.bcDomainEnabledState.get(index) ?? true;
    this.bcDomainEnabledState.set(index, !currentState);
    this.optionsForm.markAsDirty();
    this.optionsForm.markAsTouched();
  }

  isBcDomainEnabled(index: number): boolean {
    return this.bcDomainEnabledState.get(index) ?? true;
  }

  onDomainChange(): void {
    this.optionsForm.markAsDirty();
    this.optionsForm.markAsTouched();
  }

  async save(): Promise<void> {
    if (this.optionsForm.invalid || this.optionsForm.pristine) {
      return;
    }

    this.isSaving = true;
    try {
      // Save ignored domains
      const ignoredDomains = this.ignoredDomainsArray.controls
        .map(c => c.value)
        .filter(v => v && v.trim());
      
      const uniqueIgnoredDomains = [...new Set(ignoredDomains)];
      await this.userConfigService.saveIgnoredDomains(uniqueIgnoredDomains);

      // Save battle card domains - only save enabled ones
      const bcDomains = this.bcDomainsArray.controls
        .map((c, index) => ({ value: c.value, enabled: this.bcDomainEnabledState.get(index) ?? true }))
        .filter(d => d.enabled && d.value && d.value.trim())
        .map(d => d.value);
      
      const uniqueBcDomains = [...new Set(bcDomains)];
      const bcIgnoreDefault = this.bcIgnoreDefaultDomain.value;
      
      await this.userConfigService.saveBattleCardDomains(bcIgnoreDefault, uniqueBcDomains);

      // Remove disabled domains from the form array after successful save
      for (let i = this.bcDomainsArray.length - 1; i >= 0; i--) {
        if (!this.bcDomainEnabledState.get(i)) {
          this.bcDomainsArray.removeAt(i);
          this.bcDomainEnabledState.delete(i);
        }
      }

      // Reindex the enabled state map
      const newEnabledState = new Map<number, boolean>();
      let newIndex = 0;
      this.bcDomainsArray.controls.forEach((_, i) => {
        newEnabledState.set(newIndex, true);
        newIndex++;
      });
      this.bcDomainEnabledState = newEnabledState;

      this.optionsForm.markAsPristine();
      this.notificationService.notify('Settings saved successfully');
      
      this.amplitudeService.trackEvent('settings_saved', {
        ignored_domains_count: uniqueIgnoredDomains.length,
        bc_domains_count: uniqueBcDomains.length,
        bc_ignore_default: bcIgnoreDefault
      });
    } catch (error) {
      console.error('Error saving config:', error);
      this.notificationService.notify('Failed to save settings');
    } finally {
      this.isSaving = false;
    }
  }

  onBcIgnoreDefaultToggle(): void {
    this.optionsForm.markAsDirty();
  }
}
